import React, { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { Form, message, Modal, Typography } from "antd";
import dayjs from "dayjs";
import { errorMessage, successMessage } from "../../../utils/notificationUtils";
import {
  getAllStarhealthDocumentsData,
  deleteUploadedDocument,
  downloadDocument,
  updateAllDocumentsData,
  submitApproval,
} from "../../../apis/patientService";
import {
  getBasicDetails,
  getDeficiencyQuestionData,
  getTimelineDetails,
} from "../../../models/patientSlice";
import { fetchStepperData } from "../../../models/preAuthSlice";
import DocumentDeficiencyModal from "../../../components/patient/DocumentDeficiencyModal";
import FilePreviewModal from "../../../components/patient/PreAuth-query/FilePreviewModal";
import PatientDetailsStageDocument from "../../../components/modals/PatientDetailsStageDocument";
import ConfirmSubmissionModalStage from "../../../components/modals/ConfirmSubmissionModalStage";
import { UploadDocument } from "../../../models/EnhancementSlice";
import EchsDischargeForm from "../../../components/patient/Discharge/Echs/EchsDischargeForm";
import {
  intimateSubmitDischargeEchs,
  submitDischargeDataEchs,
  submitDischargeDataInEdgeProEchs,
} from "../../../models/DischargeSlice";
import {
  dischargeServiceEchs,
  getDigitallySignedDocument,
  submitApprovalEchs,
} from "../../../apis/SchemeService/ECHS/echsService";
import MultipleFilesPreviewModal from "../../../components/patient/PreAuth-query/MultipleFilesPreviewModal";
import Loader from "../../../utils/Spinners";
import { Spin } from "antd";
import { CheckCircleTwoTone, LoadingOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { Space } from "antd";
import { errorMessageModal } from "../../../utils/modalMessage";

interface Document {
  id: number;
  type?: string;
  file?: any;
  documentId?: any;
  label?: string;
  labelId?: string | number;
  documentName?: string;
  requestDocument?: boolean;
  responseDocument?: boolean;
}

interface IDocumentFields {
  documentType: string;
  documentUrl: string;
  documentName: string;
  documentLabelType?: string;
  id?: number;
  deficiencyCheckStatus?: boolean;
}

interface PayloadSchema {
  casePreAuthAttachments: IDocumentFields[];
  caseEnhancementsAttachments: IDocumentFields[];
  caseDischargeAttachments: IDocumentFields[];
  caseClaimAttachments: IDocumentFields[];
}

type DischargeEchsContainerProps = {
  activeKey: any;
  sseData?: any;
  sseUpdateCount?: any;
};

const { Text, Title } = Typography;

const DischargeEchsContainer: FC<DischargeEchsContainerProps> = ({
  activeKey,
  sseData,
  sseUpdateCount,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { patients, enhancement, auth, preAuth } = useSelector(
    (state: RootState) => state,
  );

  const [form] = Form.useForm();

  const [isDischargeLoading, setIsDischargeLoading] = useState<boolean>(false);

  // Document related state
  const [documentTypes, setDocumentTypes] = useState<Document[]>([]);
  const [documentUrls, setDocumentUrls] = useState<
    {
      documentType: string;
      documentName: string;
      documentUrl: string;
      id: number;
      referenceType: string | null;
      documentLabelType: any;
      deficiencyCheckStatus: any;
      remarks: string | null;
    }[]
  >([]);
  const [selectedDocument, setSelectedDocument] =
    useState<IDocumentFields | null>(null);
  const [deficiencyModal, setDeficiencyModal] = useState<boolean>(false);
  const [previewDocument, setPreviewDocument] = useState<boolean>(false);
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState<string>("");
  const [documentName, setDocumentName] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<{
    file: File;
    documentType: string;
  } | null>(null);
  const [previewFile, setPreviewFile] = useState({
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  });
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [
    documentRequestResponseDocuments,
    setDocumentRequestResponseDocuments,
  ] = useState<any>();
  const [clickedActiveTab, setClickedActiveTab] = useState<any>();
  const [clickedActiveDocumentUrl, setClickedActiveDocumentUrl] =
    useState<any>();
  const [fetchingDeficiencyData, setFetchingDeficiencyData] =
    useState<boolean>(false);
  const [removedDiagnoses, setRemovedDiagnoses] = useState<any[]>([]);

  // Form and UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [confirmationState, setConfirmationState] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [portalSubmission, setPortalSubmission] = useState<boolean>(false);
  const [caseDischargeId, setCaseDischargeId] = useState<any>(null);
  const [caseDischargeData, setCaseDischargeData] = useState<any>();
  const [isIntimating, setIsIntimating] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const [isApprovedInitiated, setIsApprovedInitiated] =
    useState<boolean>(false);
  const [uploadFileList, setUploadFileList] = useState<any[]>([]);

  const [showUnsignedDocumentsModal, setShowUnsignedDocumentsModal] =
    useState<boolean>(false);
  const [unsignedDocumentsCount, setUnsignedDocumentsCount] =
    useState<number>(0);
  const [digitallySignedDocuments, setDigitallySignedDocuments] = useState<any>(
    [],
  );
  const [isFetchingDigitallySignedDocs, setIsFetchingDigitallySignedDocs] =
    useState<boolean>(false);
  const [isAllDigitallySignedModal, setIsAllDigitallySignedModal] =
    useState<boolean>(false);

  const schemeMetaDataId =
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;
  const caseId = patients?.fetchBasicDetails?.casesResponse?.id;

  const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;

  const [isErrorState, setIsErrorState] = useState<boolean>(false);
  const lastErrorMessageRef = useRef<string | null>(null);

  useEffect(() => {
    if (isDischargeTabActive()) {
      lastErrorMessageRef.current = null;
    }
  }, [activeKey]);

  const isDischargeTabActive = () => {
    if (!activeKey || activeKey.length === 0) return false;

    const currentStageId =
      typeof activeKey === "string"
        ? parseInt(activeKey)
        : parseInt(activeKey?.[0]);

    const currentStage = preAuth?.stepperData?.visitStages?.find(
      (stage: any) => stage?.visitStageId === currentStageId,
    );

    setIsErrorState(currentStage?.state?.toLowerCase() === "error");

    return currentStage?.visitStage?.toUpperCase() === "DISCHARGE";
  };

  const fetchStepperForStatus = async () => {
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
      const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
      await dispatch(fetchStepperData({ caseId: caseId, visitId: visitId }));
    }
  };

  useEffect(() => {
    if (
      isErrorState &&
      caseDischargeData?.caseDischargeInitiateError &&
      caseDischargeData?.caseDischargeInitiateError !==
        lastErrorMessageRef.current &&
      isDischargeTabActive()
    ) {
      errorMessageModal(caseDischargeData?.caseDischargeInitiateError);
      lastErrorMessageRef.current =
        caseDischargeData?.caseDischargeInitiateError;
    }
  }, [caseDischargeData, isErrorState, activeKey]);

  // Fetch documents on mount and when activeKey changes
  useEffect(() => {
    if (activeKey && activeKey?.length > 0 && isDischargeTabActive())
      fetchDocuments();
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
    activeKey,
  ]);

  useEffect(() => {
    if (activeKey && activeKey?.length > 0 && isDischargeTabActive()) {
      // fetchStepperForStatus();
      // getTimelineDetail();
    }
  }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id,
    activeKey,
  ]);

  useEffect(() => {
    if (caseDischargeData) {
      setPortalSubmission(caseDischargeData?.submitted);
    }
  }, [caseDischargeData]);

  // Process document types into request/response documents
  useEffect(() => {
    const requestResponseDocuments = {
      requestedDocuments: [] as Document[],
      responseDocuments: [] as Document[],
    };

    documentTypes?.forEach((document) => {
      if (document?.requestDocument) {
        requestResponseDocuments.requestedDocuments.push(document);
      } else if (document?.responseDocument) {
        requestResponseDocuments.responseDocuments.push(document);
      }
    });

    setDocumentRequestResponseDocuments(requestResponseDocuments);
  }, [documentTypes]);

  useEffect(() => {
    const listOfUploadedDocuments = documentTypes?.flatMap(
      (item: any) =>
        item?.documentList?.map((doc: any) => ({
          id: doc?.id,
          documentName: doc?.documentName,
          documentType: doc?.documentType,
          documentUrl: doc?.documentUrl,
          documentLabelType: doc?.documentLabelType,
          deficiencyCheckStatus: doc?.deficiencyCheckStatus,
        })) || [],
    );

    setDocumentUrls(listOfUploadedDocuments);
  }, [documentTypes]);

  // Document handling functions
  const fetchDocuments = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId &&
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      try {
        setLoading(true);
        const AllDocumentList = await getAllStarhealthDocumentsData(
          patients?.fetchBasicDetails?.casesResponse?.id,
          patients?.fetchBasicDetails?.visitInfoResponse?.id,
          patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        );
        setDocumentTypes(AllDocumentList?.["Discharge"]);
      } catch (err) {
        errorMessage("Please, Try again after sometime");
      } finally {
        setLoading(false);
      }
    }
  };

  const [selectedFiles, setSelectedFiles] = useState<
    { file: File; documentType: string }[]
  >([]);
  const [previewFiles, setPreviewFiles] = useState<
    {
      url: string;
      name: string;
      showUpload: boolean;
      documentType: string;
      isMultiple: boolean;
    }[]
  >([]);

  useEffect(() => {
    if (!previewModalVisible) {
      setSelectedFiles([]);
    }
  }, [previewModalVisible]);

  const handleFileChange =
    (documentType: string, isMultiple: boolean) => (e: any) => {
      if (isMultiple) {
        const files = e?.fileList || [];

        if (e?.fileList[0]?.type !== "application/pdf") {
          errorMessage("File Must be PDF only");
          return;
        }

        setUploadFileList(files);
        if (files.length === 0) return;

        const selected = files?.map((f: any) => ({
          file: f.originFileObj,
          documentType,
        }));

        const previews = files?.map((f: any) => ({
          url: URL.createObjectURL(f.originFileObj),
          name: f.name,
          showUpload: true,
          documentType,
          isMultiple: isMultiple,
        }));

        setSelectedFiles(selected);
        setPreviewFiles(previews);
        setPreviewModalVisible(true);
      } else {
        if (Array.isArray(e)) {
          return e;
        }

        if (e.file?.type !== "application/pdf") {
          errorMessage("File Must be PDF only");
          return;
        }

        if (e?.file) {
          setSelectedFile({ file: e.file, documentType: documentType });
          setPreviewFile({
            url: URL.createObjectURL(e.file),
            name: e.file.name,
            showUpload: true,
            documentType: documentType,
          });
          setPreviewModalVisible(true);
        }
        return e?.target?.files;
      }
    };

  // const handleFileChange = (documentType: string) => (e: any) => {
  //   if (Array.isArray(e)) {
  //     return e;
  //   }
  //   if (e?.file) {
  //     setSelectedFile({ file: e.file, documentType: documentType });
  //     setPreviewFile({
  //       url: URL.createObjectURL(e.file),
  //       name: e.file.name,
  //       showUpload: true,
  //       documentType: documentType,
  //     });
  //     setPreviewModalVisible(true);
  //   }
  //   return e?.target?.files;
  // };

  const handleUpload = async () => {
    if (selectedFiles?.length > 0) {
      const CollectPostData = new FormData();
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        CollectPostData.append(
          "hospitalId",
          patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        CollectPostData.append(
          "patientId",
          patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.casesResponse?.id) {
        CollectPostData.append(
          "caseId",
          patients?.fetchBasicDetails?.casesResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        CollectPostData.append(
          "visitId",
          patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
        );
      }
      CollectPostData.append("documentType", previewFiles?.[0]?.documentType);

      selectedFiles?.map((fileObj, index) =>
        CollectPostData.append(`file${index}`, fileObj.file),
      );

      // if (selectedFiles?.length > 0) {
      //   selectedFiles.forEach((fileObj, index) => {
      //     // Extract the actual File object from your structure
      //     const actualFile = fileObj.file; // This is the originFileObj (actual File)

      //     if (actualFile instanceof File) {
      //       CollectPostData.append("files", actualFile);
      //     }
      //   });
      // }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: auth.accessToken || "",
          }),
        ).unwrap();

        // const updatedDocuments = updateOrAddDocument(documentUrls, documentRes);
        const updatedDocuments = [...documentUrls, ...documentRes];
        //     setDocumentUrls(updatedDocuments);

        setDocumentUrls(updatedDocuments);
        setPreviewModalVisible(false);
        message.success(
          `${documentRes?.[0]?.documentLabelType} uploaded successfully`,
        );
        await handleAttachDocuments(documentRes);
        setUploadFileList([]);
        setSelectedFiles([]);
        setPreviewFiles([]);
      } catch (error) {
        console.error("Failed to upload document:", error);
      }
    } else {
      const CollectPostData = new FormData();
      if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
        CollectPostData.append(
          "hospitalId",
          patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.patientResponse?.id) {
        CollectPostData.append(
          "patientId",
          patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.casesResponse?.id) {
        CollectPostData.append(
          "caseId",
          patients?.fetchBasicDetails?.casesResponse?.id?.toString(),
        );
      }
      if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
        CollectPostData.append(
          "visitId",
          patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
        );
      }
      CollectPostData.append("documentType", previewFile.documentType);

      if (selectedFile?.file) {
        CollectPostData.append("file", selectedFile?.file);
      }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: auth.accessToken || "",
          }),
        ).unwrap();

        const updatedDocuments = [...documentUrls, ...documentRes];
        // updatedDocuments.push({
        //   documentType: documentRes?.documentType,
        //   documentName: documentRes?.documentName,
        //   documentUrl: documentRes?.documentUrl,
        //   documentLabelType: documentRes?.documentLabelType,
        //   id: documentRes?.id,
        //   deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
        //   referenceType: null,
        //   remarks: null,
        // });
        setDocumentUrls(updatedDocuments);
        setPreviewModalVisible(false);
        message.success(
          `${documentRes?.[0]?.documentLabelType} uploaded successfully`,
        );
        await handleAttachDocuments(documentRes);
        setSelectedFile(null);
        setPreviewFile({
          url: "",
          name: "",
          showUpload: false,
          documentType: "",
        });
      } catch (error) {
        console.error("Failed to upload document:", error);
      }
    }
  };
  // const handleUpload = async () => {
  //   const CollectPostData = new FormData();
  //   if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
  //     CollectPostData.append(
  //       "hospitalId",
  //       patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.patientResponse?.id) {
  //     CollectPostData.append(
  //       "patientId",
  //       patients?.fetchBasicDetails?.patientResponse?.id?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.casesResponse?.id) {
  //     CollectPostData.append(
  //       "caseId",
  //       patients?.fetchBasicDetails?.casesResponse?.id?.toString()
  //     );
  //   }
  //   if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
  //     CollectPostData.append(
  //       "visitId",
  //       patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString()
  //     );
  //   }
  //   CollectPostData.append("documentType", previewFile.documentType);

  //   if (selectedFile?.file) {
  //     CollectPostData.append("file", selectedFile?.file);
  //   }

  //   try {
  //     const documentRes = await dispatch(
  //       UploadDocument({
  //         formData: CollectPostData,
  //         accessToken: auth.accessToken || "",
  //       })
  //     ).unwrap();

  //     const updatedDocuments = [...documentUrls];
  //     updatedDocuments.push({
  //       documentType: documentRes?.documentType,
  //       documentName: documentRes?.documentName,
  //       documentUrl: documentRes?.documentUrl,
  //       documentLabelType: documentRes?.documentLabelType,
  //       id: documentRes?.id,
  //       deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
  //       referenceType: null,
  //       remarks: null,
  //     });

  //     setDocumentUrls(updatedDocuments);
  //     setPreviewModalVisible(false);
  //     message.success(
  //       `${documentRes?.documentLabelType} uploaded successfully`
  //     );
  //   } catch (error) {
  //     console.error("Failed to upload document:", error);
  //   }
  // };

  const fetchDataForSse = async () => {
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      // dispatch(
      //   getBasicDetails({
      //     id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
      //   })
      // );
    }
    getDischargeData();
    fetchStepperForStatus();
    await getTimelineDetail();
    fetchDocuments();
  };

  useEffect(() => {
    if (sseData) {
      fetchDataForSse();
    }
  }, [sseData, sseUpdateCount]);

  const handleOpen = (docType: any, documentUrl: any) => {
    setPreviewDocument(true);
    setClickedActiveTab(docType);
    setClickedActiveDocumentUrl(documentUrl);
  };

  const handleCancelPreview = () => {
    setPreviewDocument(false);
    setPreviewDocumentUrl("");
    setDocumentName("");
  };

  const handleDownloadDocument = async (
    fileName: string,
    url: string | undefined,
  ) => {
    if (!url) {
      errorMessage("Document URL is missing");
      return;
    }

    try {
      await downloadDocument(fileName, url);
      successMessage("Document Downloaded Successfully");
    } catch (err) {
      errorMessage("Please, Try again later");
    }
  };

  const handleDeleteList = async (
    documentType: string,
    documentUrl: string,
    i: number,
  ) => {
    const combinedUrls = [...documentUrls];
    const deleteDocument = combinedUrls.find(
      (doc) => doc.documentUrl === documentUrl,
    );
    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };

    const newUpdatedDocumentList = combinedUrls.filter(
      (doc) => doc.documentUrl !== documentUrl,
    );
    setDocumentUrls(newUpdatedDocumentList);

    if (deleteDocument?.documentUrl) {
      try {
        await deleteUploadedDocument(deleteForm);
      } catch (error) {
        errorMessage("Failed to delete document");
      }
    }
  };

  const handleDeficiencyModalOpen = async (
    documentUrl: string,
    documentId: number,
    document: any,
  ) => {
    try {
      if (documentId) {
        setFetchingDeficiencyData(true);
        await dispatch(getDeficiencyQuestionData(document?.id));
        setSelectedDocument(document);
        setFetchingDeficiencyData(false);
        setPreviewDocumentUrl(documentUrl);
      }
    } catch (err) {
      errorMessage("Please, Try again after sometime");
      setSelectedDocument(null);
      setFetchingDeficiencyData(false);
    }
    setPreviewDocumentUrl(documentUrl);
    setSelectedDocument(document);
  };

  const closeDeficiencyModal = () => {
    setDeficiencyModal(false);
    setDocumentName("");
  };

  const validateRequiredDocuments = (
    mandatoryDocuments: any,
    documentUrls: any,
  ) => {
    for (const doc of mandatoryDocuments) {
      if (doc.required) {
        const isUploaded = documentUrls?.some(
          (uploadedDoc: any) => uploadedDoc.documentLabelType === doc.label,
        );
        if (!isUploaded) {
          return false;
        }
      }
    }
    return true;
  };

  // Form submission handlers
  const handleSubmit = async () => {
    try {
      // await form.validateFields();
      const values = await form.getFieldsValue(true);
      const currentDiagnoses = values?.provisionalDiagnosis || [];
      const allDiagnoses = [
        ...currentDiagnoses?.map((d: any) => ({ ...d, status: true })),
        // ...removedDiagnoses,
      ];

      const updatedValues = {
        ...values,
        dateOfAdmission: values?.dateOfAdmission
          ? dayjs(values?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss").format(
              "DD-MM-YYYY HH:mm:ss",
            )
          : null,
        dateOfDischarge: values?.dateOfDischarge
          ? dayjs(values?.dateOfDischarge, "DD-MM-YYYY HH:mm:ss").format(
              "DD-MM-YYYY HH:mm:ss",
            )
          : null,
        billDate: values?.billDate
          ? dayjs(values?.billDate, "DD-MM-YYYY HH:mm:ss").format(
              "DD-MM-YYYY HH:mm:ss",
            )
          : null,
        discharge:
          values?.radioButtonForDischargeAndDeath === "true" ? false : true,
        death:
          values?.radioButtonForDischargeAndDeath === "true" ? true : false,
        hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
        schemeId: patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
        documentUrls,
        roomType: {
          id: null,
          name: values?.roomType,
        },
        provisionalDiagnosis: allDiagnoses,
      };

      setIsSaving(true);
      const res = await dispatch(
        submitDischargeDataInEdgeProEchs({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          portalMetaDataId:
            patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds?.[0],
          caseDischargeId,
          dischargeData: updatedValues,
        }),
      );
      setIsSaving(false);

      if (submitDischargeDataInEdgeProEchs.fulfilled.match(res)) {
        successMessage("Discharge Saved Successfully");
        setConfirmationModal(false);
        await refreshData();
      } else if (submitDischargeDataInEdgeProEchs.rejected.match(res)) {
        errorMessage("Please, Try again later");
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessage("Please, Fill all details before submitting");
      } else {
        errorMessage("Please, Try again later");
      }
      setIsSaving(false);
      setConfirmationModal(false);
    }
  };

  const handleIntimate = async () => {
    try {
      // await form.validateFields();
      const values = await form.getFieldsValue(true);
      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        const currentDiagnoses = values?.provisionalDiagnosis || [];
        const allDiagnoses = [
          ...currentDiagnoses?.map((d: any) => ({ ...d, status: true })),
          // ...removedDiagnoses,
        ];
        const updatedValues = {
          ...values,
          dateOfAdmission: values?.dateOfAdmission
            ? dayjs(values?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          dateOfDischarge: values?.dateOfDischarge
            ? dayjs(values?.dateOfDischarge, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          billDate: values?.billDate
            ? dayjs(values?.billDate, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          discharge:
            values?.radioButtonForDischargeAndDeath === "true" ? false : true,
          death:
            values?.radioButtonForDischargeAndDeath === "true" ? true : false,
          hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          documentUrls,
          action: "intimate",
          roomType: {
            id: null,
            name: values?.roomType,
          },
          provisionalDiagnosis: allDiagnoses,
        };

        setIsIntimating(true);
        const res = await dispatch(
          intimateSubmitDischargeEchs({
            caseId: patients?.fetchBasicDetails?.casesResponse?.id,
            visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            intimationData: updatedValues,
          }),
        );
        setIsIntimating(false);

        if (intimateSubmitDischargeEchs.fulfilled.match(res)) {
          successMessage("Discharge Sent To QC Successfully");
          setConfirmationModal(false);
          await refreshData();
        } else if (intimateSubmitDischargeEchs.rejected.match(res)) {
          errorMessage("Please, Try again later");
          setConfirmationModal(false);
        }
      } else {
        errorMessage("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsIntimating(false);
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessage("Please, Fill all details before submitting");
      } else {
        errorMessage("Please, Try again later");
      }
      setIsIntimating(false);
      setConfirmationModal(false);
    }
  };

  const handleSubmitOnPortal = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);

      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        const currentDiagnoses = values?.provisionalDiagnosis || [];
        const allDiagnoses = [
          ...currentDiagnoses?.map((d: any) => ({ ...d, status: true })),
          // ...removedDiagnoses,
        ];
        const updatedValues = {
          ...values,
          dateOfAdmission: values?.dateOfAdmission
            ? dayjs(values?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          dateOfDischarge: values?.dateOfDischarge
            ? dayjs(values?.dateOfDischarge, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          billDate: values?.billDate
            ? dayjs(values?.billDate, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          discharge:
            values?.radioButtonForDischargeAndDeath === "true" ? false : true,
          death:
            values?.radioButtonForDischargeAndDeath === "true" ? true : false,
          hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          documentUrls,
          roomType: {
            id: null,
            name: values?.roomType,
          },
          integratedWithPortal:
            patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal,
          provisionalDiagnosis: allDiagnoses,
        };

        setIsSubmitting(true);
        const res = await dispatch(
          submitDischargeDataEchs({
            caseId: patients?.fetchBasicDetails?.casesResponse?.id,
            visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
            portalMetaDataId:
              patients?.fetchBasicDetails?.casesResponse
                ?.portalMetaDataIds?.[0],
            caseDischargeId,
            dischargeData: updatedValues,
          }),
        );
        setIsSubmitting(false);

        if (submitDischargeDataEchs.fulfilled.match(res)) {
          successMessage("Discharge Submitted Successfully");
          setConfirmationModal(false);
          await refreshData();
        } else if (submitDischargeDataEchs.rejected.match(res)) {
          errorMessage("Please, Try again later");
          setConfirmationModal(false);
        }
      } else {
        errorMessage("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsSubmitting(false);
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessage("Please, Fill all details before submitting");
      } else {
        errorMessage("Please, Try again later");
      }
      setIsSubmitting(false);
      setConfirmationModal(false);
    } finally {
      setIsFetchingDigitallySignedDocs(false);
    }
  };

  const handleApprove = async () => {
    try {
      await form.validateFields();
      const values = await form.getFieldsValue(true);

      if (
        validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        const currentDiagnoses = values?.provisionalDiagnosis || [];
        const allDiagnoses = [
          ...currentDiagnoses?.map((d: any) => ({ ...d, status: true })),
          // ...removedDiagnoses,
        ];
        const updatedValues = {
          ...values,
          dateOfAdmission: values?.dateOfAdmission
            ? dayjs(values?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          dateOfDischarge: values?.dateOfDischarge
            ? dayjs(values?.dateOfDischarge, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          billDate: values?.billDate
            ? dayjs(values?.billDate, "DD-MM-YYYY HH:mm:ss").format(
                "DD-MM-YYYY HH:mm:ss",
              )
            : null,
          discharge:
            values?.radioButtonForDischargeAndDeath === "true" ? false : true,
          death:
            values?.radioButtonForDischargeAndDeath === "true" ? true : false,
          hospitalId: patients?.fetchBasicDetails?.casesResponse?.healthCareId,
          schemeId:
            patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId,
          documentUrls,
          roomType: {
            id: null,
            name: values?.roomType,
          },
          provisionalDiagnosis: allDiagnoses,
        };

        setIsApprovedInitiated(true);
        const formData = {
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
          stage: "Discharge",
          approved: true,
          dischargeRequest: updatedValues,
          caseDischargeId,
        };

        const res = await submitApprovalEchs(formData);
        setIsApproved(true);
        setIsApprovedInitiated(false);
        successMessage("Discharge Approved Successfully");
        setConfirmationModal(false);
        await refreshData();
      } else {
        errorMessage("Please, Upload all required documents");
        setConfirmationModal(false);
        setIsApprovedInitiated(false);
        setIsApproved(false);
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessage("Please, fill all details before submitting");
        setIsApprovedInitiated(false);
        setIsApproved(false);
      } else {
        errorMessage("Please, Try again later");
        setIsApprovedInitiated(false);
        setIsApproved(false);
      }
      setConfirmationModal(false);
    }
  };

  const handleAttachDocuments = async (updatedDocumentUrls: any) => {
    const payloadSchema: {
      casePreAuthAttachments: any[];
      caseEnhancementsAttachments: any[];
      caseDischargeAttachments: any[];
      caseClaimAttachments: any[];
    } = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
    };

    payloadSchema["caseDischargeAttachments"] = updatedDocumentUrls;

    if (caseId && visitId && schemeMetaDataId) {
      try {
        const AllDocumentList = await updateAllDocumentsData(
          caseId,
          visitId,
          payloadSchema,
        );
        message.success(`documents saved successfully`);
        fetchDocuments();
      } catch (error) {
        message.error(`Something went wrong`);
      }
    }
  };

  const handleSaveData = async () => {
    const payloadSchema: PayloadSchema = {
      casePreAuthAttachments: [],
      caseEnhancementsAttachments: [],
      caseDischargeAttachments: [],
      caseClaimAttachments: [],
    };

    const updatedDocumentUrls = documentUrls?.filter((document: any) =>
      documentRequestResponseDocuments["responseDocuments"]?.some(
        (doc: any) => doc?.label === document?.documentLabelType,
      ),
    );

    payloadSchema.caseDischargeAttachments = updatedDocumentUrls;

    const caseId = patients?.fetchBasicDetails?.casesResponse?.id;
    const visitId = patients?.fetchBasicDetails?.visitInfoResponse?.id;
    const schemeMetaDataId =
      patients?.fetchBasicDetails?.casesResponse?.schemeMetaDataId;

    if (caseId && visitId && schemeMetaDataId) {
      try {
        await updateAllDocumentsData(caseId, visitId, payloadSchema);
        message.success("Response documents saved successfully");
        fetchDocuments();
      } catch (error) {
        message.error("Something went wrong");
      }
    }
  };

  // Helper functions
  const refreshData = async () => {
    await getDischargeData();
    await fetchDocuments();
    await fetchStepperForStatus();
    await getTimelineDetail();
    if (visitId) {
      dispatch(
        getBasicDetails({
          id: visitId,
        }),
      );
    }
    setConfirmationModal(false);
  };

  const getTimelineDetail = async () => {
    if (
      patients?.fetchBasicDetails?.casesResponse?.id &&
      patients?.fetchBasicDetails?.visitInfoResponse?.id
    ) {
      await dispatch(
        getTimelineDetails({
          caseId: patients?.fetchBasicDetails?.casesResponse?.id,
          visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
        }),
      );
    }
  };

  const handleError = (err: any) => {
    if (err?.errorFields) {
      errorMessage("Please fill all required fields");
    } else {
      errorMessage(err?.message || "Please try again later");
    }
  };

  const handleSaveConfirmationModal = async (state: string) => {
    setIsFetchingDigitallySignedDocs(true);

    if (state === "submit") {
      const payload =
        documentUrls?.map((document: any) => ({
          documentUrl: document?.documentUrl,
          documentLabelType: document?.documentLabelType,
          documentName: document?.documentName,
          documentId: document?.id,
        })) || [];

      const res = await getDigitallySignedDocument(payload);
      setIsFetchingDigitallySignedDocs(false);
      const results = res?.results || [];

      setDigitallySignedDocuments(results);

      const unsigned = results.filter((doc: any) => !doc?.digitallySigned);

      if (unsigned?.length > 0) {
        setUnsignedDocumentsCount(unsigned.length);
        setShowUnsignedDocumentsModal(true);
        return;
      } else {
        setIsAllDigitallySignedModal(true);
        return;
      }
    }

    if (state === "intimate") {
      try {
        await form.validateFields();
      } catch (err: any) {
        setIsFetchingDigitallySignedDocs(false);
        if (err?.errorFields) {
          errorMessage("Please, Fill all details before submitting");
        } else {
          errorMessage("Please, Try again later");
        }
        return;
      }
      if (
        !validateRequiredDocuments(
          documentRequestResponseDocuments["requestedDocuments"],
          documentUrls,
        )
      ) {
        setIsFetchingDigitallySignedDocs(false);
        errorMessage("Please, Upload all required documents");
        return;
      }
    }

    setConfirmationModal(true);
    setConfirmationState(state);
    setIsFetchingDigitallySignedDocs(false);

    switch (state) {
      case "save":
        setTitle("Do you want to save Discharge?");
        break;
      case "update":
        setTitle("Do you want to update Discharge?");
        break;
      case "intimate":
        setTitle("Do you want to send Discharge for QC?");
        break;
      case "submit":
        setTitle("Do you want to submit Discharge?");
        break;
      case "approve":
        setTitle("Do you want to approve Discharge?");
        break;
    }
  };

  const handleSave = async () => {
    switch (confirmationState) {
      case "save":
      case "update":
        await handleSubmit();
        break;
      case "intimate":
        await handleIntimate();
        break;
      case "submit":
        await handleSubmitOnPortal();
        break;
      case "approve":
        await handleApprove();
        break;
    }
    setConfirmationModal(false);
  };

  const getDischargeData = async () => {
    try {
      setIsDischargeLoading(true);
      const res = await dischargeServiceEchs.getDischargeData(
        patients?.fetchBasicDetails?.casesResponse?.id,
        patients?.fetchBasicDetails?.visitInfoResponse?.id,
      );

      if (res && typeof res === "object") {
        setCaseDischargeData(res);
        setCaseDischargeId(res?.id);
        setIsApproved(res?.approved);

        const updatedValues = {
          ...res,
          dateOfAdmission:
            res?.dateOfAdmission &&
            dayjs(res?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss", true).isValid()
              ? dayjs(res?.dateOfAdmission, "DD-MM-YYYY HH:mm:ss")
              : null,
          dateOfDischarge:
            res?.dateOfDischarge &&
            dayjs(res?.dateOfDischarge, "DD-MM-YYYY HH:mm:ss", true).isValid()
              ? dayjs(res?.dateOfDischarge, "DD-MM-YYYY HH:mm:ss")
              : null,
          billDate:
            res?.billDate &&
            dayjs(res?.billDate, "DD-MM-YYYY HH:mm:ss", true).isValid()
              ? dayjs(res?.billDate, "DD-MM-YYYY HH:mm:ss")
              : null,
          radioButtonForDischargeAndDeath: res?.discharge
            ? "false"
            : res?.death
              ? "false"
              : null,
          roomType: res?.roomType?.name,
          dischargeType: res?.id ? res?.dischargeType : "Normal Discharge",
        };

        form.setFieldsValue(updatedValues);
        setIsDischargeLoading(false);
      }
      setIsDischargeLoading(false);
    } catch (err) {
      setIsDischargeLoading(false);
      errorMessage("Please, Try again later");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await getDischargeData();
      await fetchDocuments();
    };

    if (activeKey && activeKey?.length > 0 && isDischargeTabActive())
      fetchData();
  }, [schemeMetaDataId, activeKey]);

  return (
    <>
      {isDischargeLoading ? (
        <Loader />
      ) : (
        <EchsDischargeForm
          form={form}
          loading={loading}
          documentTypes={documentTypes}
          documentUrls={documentUrls}
          documentRequestResponseDocuments={documentRequestResponseDocuments}
          handleFileChange={handleFileChange}
          handleOpen={handleOpen}
          handleSaveData={handleSaveData}
          preAuth={preAuth}
          isApproved={isApproved}
          activeKey={activeKey}
          dischargeData={caseDischargeData}
          isEditable={!portalSubmission}
          caseDischargeId={caseDischargeId}
          portalSubmission={portalSubmission}
          isApprovedInitiated={isApprovedInitiated}
          isSubmitting={isSubmitting}
          isIntimating={isIntimating}
          isSaving={isSaving}
          handleSaveConfirmationModal={handleSaveConfirmationModal}
          caseDischargeData={caseDischargeData}
          uploadFileList={uploadFileList}
          setRemovedDiagnoses={setRemovedDiagnoses}
        />
      )}

      {selectedFile?.file && !selectedFiles?.length && (
        <FilePreviewModal
          isVisible={previewModalVisible}
          onClose={() => setPreviewModalVisible(false)}
          fileUrl={previewFile.url}
          fileName={previewFile.name}
          documentType={previewFile.documentType}
          handleUpload={handleUpload}
          showUploadButton={previewFile.showUpload}
          key={selectedFile?.file?.lastModified || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      <PatientDetailsStageDocument
        handleCancel={handleCancelPreview}
        handleDeleteDocument={handleDeleteList}
        open={previewDocument}
        handleDownloadDocument={handleDownloadDocument}
        // handleSetDocumentUrl={handleSetDocumentUrl}
        documents={documentUrls}
        handleDeficiencyModal={handleDeficiencyModalOpen}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        setDocumentName={setDocumentName}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setDocumentUrls}
        activeKey={activeKey}
        clickedActiveTab={clickedActiveTab}
        isSubmittedStatus={portalSubmission}
        fetchingDeficiencyData={fetchingDeficiencyData}
        clickedActiveDocumentUrl={clickedActiveDocumentUrl}
        isHospitalBillDischargeSummary={true}
      />

      <DocumentDeficiencyModal
        key={previewDocumentUrl}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        documentUrl={previewDocumentUrl}
        documentName={documentName}
        handleDownloadDocument={handleDownloadDocument}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={setDocumentUrls}
      />

      <ConfirmSubmissionModalStage
        open={confirmationModal}
        title={title}
        handleCancel={() => setConfirmationModal(false)}
        handleSave={handleSave}
        isLoading={
          isSaving || isIntimating || isApprovedInitiated || isSubmitting
        }
      />

      {selectedFiles?.length > 0 && (
        <MultipleFilesPreviewModal
          isVisible={previewModalVisible}
          onClose={() => {
            setPreviewModalVisible(false);
            setSelectedFiles([]);
            setUploadFileList([]);
          }}
          files={previewFiles}
          handleUpload={handleUpload}
          showUploadButton={previewFiles?.some((f) => f.showUpload)}
          key={previewFiles?.map((f) => f.name).join(",") || "no-file"}
          isLoading={enhancement?.isLoading}
        />
      )}

      <Modal
        title="Document Signature Status"
        visible={showUnsignedDocumentsModal}
        onCancel={() => setShowUnsignedDocumentsModal(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setShowUnsignedDocumentsModal(false)}
          >
            Close
          </Button>,
          <Button
            key="continue"
            type="primary"
            style={{ backgroundColor: "rgb(0, 76, 143)", color: "#fff" }}
            onClick={() => {
              setShowUnsignedDocumentsModal(false);
              setTitle("Do you want to submit Discharge?");
              setConfirmationModal(true);
              setConfirmationState("submit");
            }}
          >
            Continue Anyway
          </Button>,
        ]}
        centered
        width={700}
      >
        <div style={{ padding: "20px 0" }}>
          <p
            style={{
              fontSize: "16px",
              marginBottom: "20px",
              fontWeight: "bold",
              color: "#d32f2f",
            }}
          >
            {unsignedDocumentsCount} document(s) are NOT digitally signed
          </p>

          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {/* Digitally Signed Documents */}
            {digitallySignedDocuments?.some(
              (doc: any) => doc?.digitallySigned,
            ) && (
              <div style={{ marginBottom: "20px" }}>
                <h4 style={{ color: "#2e7d32", marginBottom: "10px" }}>
                  ✓ Digitally Signed Documents
                </h4>
                {digitallySignedDocuments
                  ?.filter((doc: any) => doc?.digitallySigned)
                  ?.map((doc: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: "#e8f5e9",
                        marginBottom: "8px",
                        borderRadius: "4px",
                        borderLeft: "4px solid #2e7d32",
                      }}
                    >
                      <span style={{ color: "#2e7d32", fontWeight: "500" }}>
                        ✓ {doc?.documentLabelType || doc?.documentName}
                      </span>
                    </div>
                  ))}
              </div>
            )}

            {/* Non-Digitally Signed Documents */}
            {digitallySignedDocuments?.some(
              (doc: any) => !doc?.digitallySigned,
            ) && (
              <div>
                <h4 style={{ color: "#d32f2f", marginBottom: "10px" }}>
                  ✗ Not Digitally Signed Documents
                </h4>
                {digitallySignedDocuments
                  ?.filter((doc: any) => !doc?.digitallySigned)
                  ?.map((doc: any, index: number) => (
                    <div
                      key={index}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: "#ffebee",
                        marginBottom: "8px",
                        borderRadius: "4px",
                        borderLeft: "4px solid #d32f2f",
                      }}
                    >
                      <span style={{ color: "#d32f2f", fontWeight: "500" }}>
                        ✗ {doc?.documentLabelType || doc?.documentName}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>

          <p style={{ color: "#666", fontSize: "14px", marginTop: "20px" }}>
            Please ensure all documents are digitally signed before initiating
            the Discharge.
          </p>
        </div>
      </Modal>

      <Modal
        open={isFetchingDigitallySignedDocs}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
        width={820}
      >
        <div
          style={{
            textAlign: "center",
            padding: "56px 32px",
            backgroundColor: "#ffffff",
          }}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 48, color: "rgb(0, 76, 143)" }}
                spin
              />
            }
          />

          <Title
            level={4}
            style={{
              marginTop: 32,
              marginBottom: 12,
              color: "rgb(0, 76, 143)",
              fontWeight: 600,
            }}
          >
            Verifying Digital Signature
          </Title>

          <Text
            style={{
              color: "rgba(0, 0, 0, 0.65)",
              fontSize: 14,
              lineHeight: 1.6,
              maxWidth: 800,
              display: "inline-block",
            }}
          >
            We are currently validating the digitally signed document. Please
            keep this page open and avoid refreshing until the process is
            complete.
          </Text>
        </div>
      </Modal>

      <Modal
        open={isAllDigitallySignedModal}
        footer={null}
        closable={true}
        maskClosable={false}
        centered
        width={520}
        onCancel={() => setIsAllDigitallySignedModal(false)}
      >
        <div style={{ textAlign: "center", padding: "24px 12px" }}>
          <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 56 }} />

          <Title level={4} style={{ marginTop: 24 }}>
            All Documents Are Digitally Signed
          </Title>

          <Text type="secondary">
            The digital signing process has been completed successfully. You may
            now continue to the next step.
          </Text>

          <Space size="middle" style={{ marginTop: 32 }}>
            <Button onClick={() => setIsAllDigitallySignedModal(false)}>
              Close
            </Button>

            <Button
              type="primary"
              size="large"
              style={{ backgroundColor: "rgb(0, 76, 143)", color: "#fff" }}
              onClick={() => {
                try {
                  setIsAllDigitallySignedModal(false);
                  setTitle("Do you want to submit Discharge?");
                  setConfirmationModal(true);
                  setConfirmationState("submit");
                } catch (err: any) {
                  if (err?.errorFields) {
                    errorMessage(
                      "Please fill all required fields before submitting",
                    );
                  } else {
                    errorMessage("Please try again later");
                  }
                }
              }}
            >
              Continue
            </Button>
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default DischargeEchsContainer;
